import describePieveWorkflowRunFailures from '@shared/pieve/describePieveWorkflowRunFailures.js';
import { PIEVE_WORKFLOW_RUN_OUTCOME } from '@shared/pieve/workflowRunOutcome.js';

const COMPLETED_WITH_FAILURES = 'Completed with failures';

// Canonical Pieve workflow-run status vocabulary (see projects/api .../createPieveWorkflowRunTimeMachineRecorder.service.js
// and components/surfaces/workflowEditorPane/lib/workflowRunStatus.js): succeeded | failed | duplicate_prevented |
// canceled | running | queued. Maps a raw run status string to its display label and badge variant.
//
// A run the organization's workflow step limit stopped is recorded `failed` with `failure_reason = task_limit_exceeded`. It
// never ran, so it is named for what stopped it rather than lumped in with runs whose steps broke.
//
// A succeeded run whose C1 outcome (meta.workflowRunSummary.outcome) is `completed_with_failures` is amber, never green: it
// finished but left items undone. Its label carries the count in words — "Completed with failures · 2 of 3 Send reminder
// email" — so the color is never the only signal; `headline` and `detail` are the two halves for a Tag and its text.
const getWorkflowRunStatusPresentation = (status, failureReason = null, workflowRunSummary = null) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();

  if (normalized === 'succeeded' && workflowRunSummary?.outcome === PIEVE_WORKFLOW_RUN_OUTCOME.completedWithFailures) {
    const detail = describePieveWorkflowRunFailures(workflowRunSummary);

    return { label: detail ? `${COMPLETED_WITH_FAILURES} · ${detail}` : COMPLETED_WITH_FAILURES, variant: 'warning', headline: COMPLETED_WITH_FAILURES, detail };
  }

  if (normalized === 'succeeded') {
    return { label: 'Succeeded', variant: 'success' };
  }

  if (
    normalized === 'failed' &&
    String(failureReason || '')
      .trim()
      .toLowerCase() === 'task_limit_exceeded'
  ) {
    return { label: 'Workflow step limit', variant: 'failure', isTaskLimit: true };
  }

  if (normalized === 'failed' || normalized === 'duplicate_prevented') {
    return { label: normalized === 'duplicate_prevented' ? 'Duplicate prevented' : 'Failed', variant: 'failure' };
  }

  if (normalized === 'canceled') {
    return { label: 'Canceled', variant: 'cancelled' };
  }

  if (normalized === 'running') {
    return { label: 'Running', variant: 'running' };
  }

  if (normalized === 'queued') {
    return { label: 'Queued', variant: 'running' };
  }

  return { label: normalized ? normalized.replace(/_/g, ' ') : 'Unknown', variant: 'neutral' };
};

export default getWorkflowRunStatusPresentation;
